/**
 * What is worth knowing when a token renewal fails.
 *
 * WHY THIS EXISTS. CAPACITOR-D: four members on a store build, all on
 * /home/notifications/, all getting the same message from the iOS plugin,
 * « L'opération n'a pas pu s'achever. (CapacitorFirebaseAuthentication.
 * RuntimeError erreur 1.) ». That message is a constant: the plugin's own
 * error type loses its text when Swift bridges it to Objective-C, so iOS
 * makes one up from the domain and the code. On the getIdToken path it can
 * only ever mean one thing, « No user is signed in. »
 *
 * Which leaves the question this file answers: WHY is nobody signed in, when
 * the application itself still holds a session. Three causes need three
 * different fixes, and the event as it arrives today cannot tell them apart:
 *
 *   a race at launch      Firebase has not restored its user YET
 *   a session really gone password changed elsewhere, token revoked, account
 *                         disabled, keychain lost
 *   the application wrong it kept a token it should have dropped
 *
 * So the next occurrence carries the facts that separate them, and the ticket
 * settles itself instead of needing another week of reading.
 *
 * NOTHING PERSONAL TRAVELS. No user id, no e-mail, no token, not one
 * character of one. Booleans, a duration and three closed vocabularies, which
 * is everything needed to tell the three causes apart and nothing more. A
 * test asserts it rather than trusting this paragraph.
 */

/** Where the renewal came from. The two callers say which. */
export type RenewalTrigger = 'start' | 'request';

/** What Firebase answered when asked whether somebody is signed in. */
export type FirebaseUserState = 'present' | 'missing' | 'unreadable';

/**
 * What waiting for Firebase to restore its session changed.
 *
 * This is what makes a « missing » conclusive rather than a question. Before
 * the wait existed, a missing user could equally be a race at launch or a
 * session really gone, and the event could not say which. Now:
 *
 *   'not-needed'  a user was already there, nothing was waited for
 *   'appeared'    nobody at first, somebody after waiting: it was a race
 *   'never-came'  nobody, even after the whole wait: the session is gone
 */
export type RestoreWait = 'not-needed' | 'appeared' | 'never-came';

/** What the application was holding when it asked. */
export type StoredTokenState = 'valid' | 'expired' | 'unreadable' | 'none';

export interface TokenRenewalFacts {
  trigger: RenewalTrigger;
  firebaseUser: FirebaseUserState;
  /** What waiting for Firebase changed. The answer to « race, or gone ». */
  restoreWait: RestoreWait;
  storedToken: StoredTokenState;
  /**
   * Milliseconds since the web view started, which for a cold start is the
   * age of the application. It does NOT reset when the application returns
   * from the background, so a large value means « not a cold start » and a
   * small one means « just launched ». That asymmetry is the whole point.
   */
  msSinceLaunch: number;
  /** The window where a race with Firebase's own restore is plausible. */
  withinFirstSeconds: boolean;
}

/**
 * How long a renewal waits for Firebase to restore its session.
 *
 * Three seconds. Long enough for a cold start on an old phone, short enough
 * that a session which really is gone costs one short pause and not a screen
 * that looks frozen. Only ever waited when Firebase says there is nobody, so
 * the ordinary case costs nothing at all.
 */
export const RESTORE_WAIT_MS = 3 * 1000;

/**
 * How long after launch a missing Firebase user can still be a race.
 *
 * Ten seconds is generous on purpose. Too short and a slow cold start on an
 * old phone reads as a real signed-out session, which is the mistake this
 * whole report exists to avoid.
 */
export const RACE_WINDOW_MS = 10 * 1000;

export interface TokenRenewalInput {
  trigger: RenewalTrigger;
  firebaseUser: FirebaseUserState;
  restoreWait?: RestoreWait;
  /** The token the application was holding, only to be measured, never sent. */
  storedToken?: string | null;
  /** Its expiry in milliseconds, or null when it could not be read. */
  expiresAt?: number | null;
  msSinceLaunch: number;
  now?: number;
}

export const storedTokenState = (token: string | null | undefined, expiresAt: number | null | undefined, now: number): StoredTokenState => {
  if (!token) {
    return 'none';
  }
  if (expiresAt === null || expiresAt === undefined) {
    return 'unreadable';
  }

  return expiresAt > now ? 'valid' : 'expired';
};

export const tokenRenewalFacts = (input: TokenRenewalInput): TokenRenewalFacts => {
  const now = input.now ?? Date.now();
  const msSinceLaunch = Math.max(0, Math.round(input.msSinceLaunch));

  return {
    trigger: input.trigger,
    firebaseUser: input.firebaseUser,
    restoreWait: input.restoreWait ?? 'not-needed',
    storedToken: storedTokenState(input.storedToken, input.expiresAt, now),
    msSinceLaunch,
    withinFirstSeconds: msSinceLaunch < RACE_WINDOW_MS,
  };
};
